<script setup lang="ts">
import { cloneEntity } from '@common/utils/EntityUtils';
import { computed, inject, onMounted, onUnmounted, ref, shallowRef, toRaw, watch } from 'vue';

import { ClientWorld } from '../ClientWorld';
import { ClientDefaults as ComponentDefaults } from '../components';
import { ClientServiceContainer } from '../services/ClientServiceContainer';
import { EntityInspectorService } from '../services/EntityInspectorService';
// Mock systems
import { AnimationSystem } from '../systems/AnimationSystem';
import { AnimationTransformSystem } from '../systems/AnimationTransformSystem';
import { FaceDecorationSystem } from '../systems/FaceDecorationSystem';
import { SkinColourSystem } from '../systems/SkinColourSystem';
import EntityRepresentation from './EntityRepresentation.vue';

const props = defineProps<{
  inspectorService: EntityInspectorService;
  world: ClientWorld;
}>();

const clientServices = inject<ClientServiceContainer>('services')!;

const activeTab = ref<'components' | 'live' | 'animation'>('components');

// Animation Inspector State
const mockWorld = shallowRef<ClientWorld | null>(null);
const mockEntityId = ref<number | null>(null);
const selectedAnimation = ref('');
let animSystem: AnimationSystem | null = null;
let animTransformSystem: AnimationTransformSystem | null = null;
let faceDecoSystem: FaceDecorationSystem | null = null;
let skinColourSystem: SkinColourSystem | null = null;

const initMockWorld = () => {
  if (mockWorld.value) return;

  const world = new ClientWorld(ComponentDefaults);
  mockWorld.value = world;

  // Manual system instantiation for local update
  animSystem = new AnimationSystem(clientServices);
  animTransformSystem = new AnimationTransformSystem(clientServices);
  faceDecoSystem = new FaceDecorationSystem(clientServices);
  skinColourSystem = new SkinColourSystem(clientServices);

  // Sync animation state
  if (!selectedAnimation.value) selectedAnimation.value = 'idle_down';
  updateMockEntity();
};

const updateMockEntity = () => {
  if (!mockWorld.value) return;

  const world = mockWorld.value;
  const selectedId = props.inspectorService.state.selectedEntityId;

  // Cleanup old state via proper world reset (resets counters and callbacks)
  world.resetWorld();
  mockEntityId.value = null;

  if (selectedId === undefined) return;

  // Clone the selected entity with all its components and children (equipment, etc.)
  // We use toRaw(props.world) to ensure we're reading from the underlying ECS state
  // without triggering Vue reactivity or operating on proxies.
  const targetId = cloneEntity(toRaw(props.world), selectedId, world);
  mockEntityId.value = targetId;

  // Apply selected animation override
  let animComp = world.getComponent(targetId, 'Animation');
  if (!animComp || animComp.name !== selectedAnimation.value) {
    if (animComp) world.removeComponent(targetId, 'Animation');
    animComp = world.createComponent('Animation', {
      name: selectedAnimation.value,
      animationFrame: 1,
      timer: 0,
      loop: true,
      isPlaying: true,
    });
    world.addComponent(targetId, animComp);
  }
};

const tickMockWorld = (deltaTime: number) => {
  const world = mockWorld.value;
  const entityId = mockEntityId.value;
  if (!world || entityId === null) return;

  // Use toRaw to ensure the systems operate on the underlying ECS world object
  // rather than a Vue reactivity proxy.
  const rawWorld = toRaw(world) as ClientWorld;
  
  animSystem?.updateEntity({
    world: rawWorld,
    entityId,
    deltaTime,
  });
  
  animTransformSystem?.updateEntity({
    world: rawWorld,
    entityId,
    deltaTime,
  });

  faceDecoSystem?.updateEntity({
    world: rawWorld,
    entityId,
    deltaTime,
  });

  skinColourSystem?.updateEntity({
    world: rawWorld,
    entityId,
    deltaTime,
  });
};

onUnmounted(() => {
  if (mockWorld.value) {
    mockWorld.value.entities.clear();
    mockWorld.value = null;
  }
  animSystem = null;
  animTransformSystem = null;
  faceDecoSystem = null;
  skinColourSystem = null;
});

watch(activeTab, (newTab) => {
  if (newTab === 'animation') {
    initMockWorld();
  }
});

watch(() => props.inspectorService.state.selectedEntityId, () => {
  if (activeTab.value === 'animation') {
    updateMockEntity();
  }
});

const animations = computed(() => {
  if (!mockWorld.value || mockEntityId.value === null) return [];
  const ssSprite = mockWorld.value.getComponent(mockEntityId.value, 'SpritesheetSprite');
  if (!ssSprite) return [];
  
  const data = clientServices.spritesheet.getSpritesheet(ssSprite.spritesheetId);
  return data ? Array.from(data.animations.keys()) : [];
});

const entities = computed(() => {
  // Access lastUpdateTick to trigger re-computation when the world updates
  props.inspectorService.state.lastUpdateTick;
  return Array.from(props.world.entities.keys()).sort((a, b) => a - b);
});

const selectedEntityComponents = computed(() => {
  // Access lastUpdateTick to trigger re-computation when the world updates
  props.inspectorService.state.lastUpdateTick;

  const id = props.inspectorService.state.selectedEntityId;
  if (id === undefined) return null;

  const entity = props.world.entities.get(id);
  if (!entity) return null;

  return Array.from(entity.values()).sort((a, b) =>
    (a.typeName as string).localeCompare(b.typeName as string),
  );
});

const totalEntities = computed(() => props.world.entities.size);

const handleKeydown = (event: KeyboardEvent) => {
  if (
    event.target instanceof HTMLInputElement ||
    event.target instanceof HTMLTextAreaElement
  ) {
    return;
  }

  if (event.code === 'KeyP') {
    event.preventDefault();
    event.stopPropagation();
    props.inspectorService.toggle();
  }
};

onMounted(() => {
  window.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown);
});

const selectEntity = (id: number) => {
  props.inspectorService.selectEntity(id);
};
</script>

<template>
  <div
    v-if="props.inspectorService.state.isOpen"
    class="entity-inspector"
  >
    <div class="inspector-header">
      <h2>Entity Inspector</h2>
      <div class="stats">
        Total: {{ totalEntities }}
      </div>
      <button
        class="close-btn"
        aria-label="Close Entity Inspector"
        @click="props.inspectorService.close()"
      >
        ×
      </button>
    </div>

    <div class="inspector-content">
      <div class="entity-list-container">
        <div class="entity-list-header">
          <span>ID</span>
          <span>Components</span>
        </div>
        <div class="entity-list">
          <div
            v-for="id in entities"
            :key="id"
            class="entity-item"
            :class="{ selected: props.inspectorService.state.selectedEntityId === id }"
            @click="selectEntity(id)"
          >
            <span class="entity-id">#{{ id }}</span>
            <span class="component-count">
              {{ props.world.entities.get(id)?.size || 0 }} comps
            </span>
          </div>
        </div>
      </div>

      <div
        v-if="selectedEntityComponents"
        class="entity-details"
      >
        <div class="details-header">
          <h3>Entity #{{ props.inspectorService.state.selectedEntityId }}</h3>
          <button
            class="deselect-btn"
            aria-label="Deselect Entity"
            @click="props.inspectorService.selectEntity(undefined)"
          >
            Deselect
          </button>
        </div>

        <div class="tabs">
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'components' }"
            @click="activeTab = 'components'"
          >
            Components
          </button>
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'live' }"
            @click="activeTab = 'live'"
          >
            Live Preview
          </button>
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'animation' }"
            @click="activeTab = 'animation'"
          >
            Animation Inspector
          </button>
        </div>

        <div
          v-if="activeTab === 'components'"
          class="component-list"
        >
          <div
            v-for="comp in selectedEntityComponents"
            :key="comp.typeName"
            class="component-item"
          >
            <div class="component-name">
              {{ comp.typeName }}
            </div>
            <pre class="component-data">{{ JSON.stringify(comp, (key, value) => (key === 'typeName' ? undefined : value), 2) }}</pre>
          </div>
        </div>

        <div
          v-else-if="activeTab === 'live'"
          class="preview-container"
        >
          <EntityRepresentation
            key="live-preview"
            :services="clientServices"
            :world="props.world"
            :entity-id="props.inspectorService.state.selectedEntityId!"
            :size="256"
            :zoom="4"
          />
          <div class="preview-info">
            Rendering exact copy of in-game state
          </div>
        </div>

        <div
          v-else-if="activeTab === 'animation'"
          class="animation-inspector"
        >
          <div class="inspector-controls">
            <div class="control-group">
              <label>Animation</label>
              <select
                v-model="selectedAnimation"
                @change="updateMockEntity"
              >
                <option
                  v-for="anim in animations"
                  :key="anim"
                  :value="anim"
                >
                  {{ anim }}
                </option>
              </select>
            </div>
            <div class="inspector-hint">
              Mock World Simulation Active
            </div>
          </div>
          
          <div class="preview-container">
            <EntityRepresentation
              v-if="mockWorld && mockEntityId !== null"
              key="animation-preview"
              :services="clientServices"
              :world="toRaw(mockWorld)!"
              :entity-id="mockEntityId"
              :size="256"
              :zoom="4"
              @tick="tickMockWorld"
            />
          </div>
        </div>
      </div>
      <div
        v-else
        class="no-selection"
      >
        Select an entity to view components
      </div>
    </div>
  </div>
</template>

<style scoped>
.entity-inspector {
  position: fixed;
  top: 20px;
  right: 20px;
  width: 600px;
  max-width: 90vw;
  height: calc(100vh - 40px);
  background: rgba(15, 20, 30, 0.95);
  border: 1px solid rgba(100, 140, 180, 0.4);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  z-index: 999999;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.6);
  font-family: 'Inter', system-ui, sans-serif;
  color: #c8d4e2;
  backdrop-filter: blur(8px);
}

.inspector-header {
  padding: 12px 16px;
  border-bottom: 1px solid rgba(100, 140, 180, 0.2);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(100, 140, 180, 0.05);
}

.inspector-header h2 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #648cb4;
}

.stats {
  font-size: 11px;
  color: #8899aa;
  font-weight: 500;
}

.close-btn {
  background: none;
  border: none;
  color: #8899aa;
  font-size: 20px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
}

.close-btn:hover {
  color: #fff;
}

.inspector-content {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.entity-list-container {
  width: 200px;
  display: flex;
  flex-direction: column;
  border-right: 1px solid rgba(100, 140, 180, 0.2);
  background: rgba(0, 0, 0, 0.2);
}

.entity-list-header {
  display: flex;
  justify-content: space-between;
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.3);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  color: #667788;
  letter-spacing: 0.5px;
}

.entity-list {
  flex: 1;
  overflow-y: auto;
  padding: 4px 0;
}

.entity-item {
  display: flex;
  justify-content: space-between;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.1s ease;
  border-left: 3px solid transparent;
}

.entity-item:hover {
  background: rgba(100, 140, 180, 0.1);
}

.entity-item.selected {
  background: rgba(100, 140, 180, 0.15);
  border-left-color: #00ffaa;
  color: #fff;
}

.entity-id {
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  color: #00ffaa;
  font-weight: 600;
}

.component-count {
  font-size: 10px;
  color: #8899aa;
}

.entity-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: rgba(20, 25, 35, 0.4);
}

.details-header {
  padding: 12px 16px;
  border-bottom: 1px solid rgba(100, 140, 180, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(0, 0, 0, 0.1);
}

.details-header h3 {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
}

.deselect-btn {
  background: rgba(100, 140, 180, 0.15);
  border: 1px solid rgba(100, 140, 180, 0.3);
  color: #8899aa;
  font-size: 10px;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.2s;
}

.deselect-btn:hover {
  background: rgba(100, 140, 180, 0.25);
  color: #fff;
}

.tabs {
  display: flex;
  background: rgba(0, 0, 0, 0.2);
  border-bottom: 1px solid rgba(100, 140, 180, 0.1);
}

.tab-btn {
  flex: 1;
  padding: 8px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  color: #8899aa;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.2s;
}

.tab-btn:hover {
  background: rgba(100, 140, 180, 0.05);
  color: #fff;
}

.tab-btn.active {
  color: #00ffaa;
  border-bottom-color: #00ffaa;
  background: rgba(0, 255, 170, 0.05);
}

.component-list {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
}

.preview-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: radial-gradient(circle at center, rgba(100, 140, 180, 0.15) 0%, transparent 70%);
}

.preview-info {
  margin-top: 16px;
  font-size: 11px;
  color: #648cb4;
  font-style: italic;
}

.animation-inspector {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.inspector-controls {
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.2);
  border-bottom: 1px solid rgba(100, 140, 180, 0.1);
}

.control-group {
  margin-bottom: 10px;
}

.control-group label {
  display: block;
  font-size: 10px;
  color: #648cb4;
  text-transform: uppercase;
  margin-bottom: 4px;
  font-weight: 700;
}

.control-group select {
  width: 100%;
  background: rgba(40, 50, 70, 0.8);
  border: 1px solid rgba(100, 140, 180, 0.3);
  color: #fff;
  padding: 6px;
  font-size: 12px;
  border-radius: 4px;
  outline: none;
}

.checkbox-label {
  display: flex !important;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  text-transform: none !important;
  color: #a0b0c0 !important;
}

.inspector-hint {
  font-size: 9px;
  color: #00ffaa;
  opacity: 0.6;
  text-align: right;
  margin-top: 4px;
}

.component-item {
  margin-bottom: 12px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(100, 140, 180, 0.15);
  border-radius: 6px;
  overflow: hidden;
}

.component-name {
  padding: 6px 10px;
  background: rgba(100, 140, 180, 0.1);
  font-size: 11px;
  font-weight: 700;
  color: #648cb4;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid rgba(100, 140, 180, 0.1);
}

.component-data {
  margin: 0;
  padding: 10px;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 11px;
  color: #a0b0c0;
  overflow-x: auto;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-all;
}

.no-selection {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #556677;
  font-size: 13px;
  font-style: italic;
  letter-spacing: 0.5px;
}

/* Custom Scrollbar */
.entity-list::-webkit-scrollbar,
.component-list::-webkit-scrollbar,
.component-data::-webkit-scrollbar {
  width: 5px;
  height: 5px;
}

.entity-list::-webkit-scrollbar-track,
.component-list::-webkit-scrollbar-track,
.component-data::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
}

.entity-list::-webkit-scrollbar-thumb,
.component-list::-webkit-scrollbar-thumb,
.component-data::-webkit-scrollbar-thumb {
  background: rgba(100, 140, 180, 0.2);
  border-radius: 10px;
}

.entity-list::-webkit-scrollbar-thumb:hover,
.component-list::-webkit-scrollbar-thumb:hover,
.component-data::-webkit-scrollbar-thumb:hover {
  background: rgba(100, 140, 180, 0.4);
}
</style>
